import { useEffect, useMemo, useRef, useState } from "react";

import { Building, Edit } from "lucide-react";

import { Select } from "antd";

import { Reactable } from "../../components/ui/reactable";

import { Button } from "../../components/ui/button";

import { MASTER_BLOCK, BRANCH } from "../../constants/api";

import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";

import { DialogModal } from "../../components/ui/dialogModal";

import { Input, Textarea } from "../../components/ui/input";

import useApiMethods from "../../lib/useApiMethods";

import ToastMsg from "../../components/common/ToastMsg";

import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { masterFormSelectProps } from "../../components/common/BranchFilterDropdown";

import { isBranchManagementEnabled } from "../../utils/customConfigSession";
import useBranchRole from "../../hooks/useBranchRole";
import {
    MASTER_BLOCK_FLOOR_NAME_MAX,
    MASTER_NOTES_MAX_LENGTH,
    masterSaveErrorMessage,
    validateMasterLocationName,
} from "./masterLocationFields";



const { Option } = Select;



const BlockMaster = () => {

    const { create, update, fetch } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    const branchEnabled = isBranchManagementEnabled();
    const { isBranchManager, branchId: loginBranchId, branchName: loginBranchName } = useBranchRole();
    const lockedBranchId = loginBranchId
        ? (Number(loginBranchId) || loginBranchId)
        : undefined;

    const [branches, setBranches] = useState([]);

    const branchListFilter = useMemo(() => {
        if (isBranchManager && lockedBranchId) {
            return { branch: lockedBranchId };
        }
        return {};
    }, [isBranchManager, lockedBranchId]);

    const [data, setData] = useState([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);

    const [loading, setLoading] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const [formData, setFormData] = useState({

        id: null,

        code: "",

        block_name: "",

        branch: undefined,

        notes: "",

        is_active: true,

    });



    const { add: addPermission, edit: editPermission } =

        checkUserPermissionFunc("master", "block", "SubMenu");



    useEffect(() => {
        if (!branchEnabled || isBranchManager) return;
        fetchRef.current(BRANCH, { drop_down: "True" })
            .then((res) => {
                const raw = res?.data?.result || res?.data || [];
                setBranches(Array.isArray(raw) ? raw : []);
            })
            .catch(() => setBranches([]));
    }, [branchEnabled, isBranchManager]);

    // Branch admin: default to own branch on add; field stays read-only.
    useEffect(() => {
        if (!branchEnabled || !isBranchManager || !lockedBranchId || !openDialog) return;
        setFormData((prev) => (prev.branch ? prev : { ...prev, branch: lockedBranchId }));
    }, [branchEnabled, isBranchManager, lockedBranchId, openDialog]);

    const resetForm = () => {

        setFormData({ id: null, code: "", block_name: "", branch: undefined, notes: "", is_active: true });

        setEditMode(false);

        setOpenDialog(false);

    };



    const openAddDialog = () => {
        setFormData({
            id: null,
            code: "",
            block_name: "",
            branch: isBranchManager && lockedBranchId ? lockedBranchId : undefined,
            notes: "",
            is_active: true,
        });
        setEditMode(false);
        setOpenDialog(true);
    };



    const columns = useMemo(() => {

        const cols = [

            { accessor: "code", Header: "Code", filterable: true },

            { accessor: "block_name", Header: "Block Name", sortable: true, filterable: true },

        ];

        if (branchEnabled) {

            cols.push({
                accessor: "branch_name",
                Header: "Branch",
                filterable: true,
                Cell: ({ value }) => value || "-",
            });

        }

        cols.push(

            { accessor: "notes", Header: "Description", Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description") },

            {

                accessor: "is_active",

                Header: "Status",

                filterable: true,

                Cell: ({ value }) => (

                    <span className={cn(

                        "px-2 py-1 text-xs font-medium rounded-full",

                        value ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"

                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"

                    )}>

                        {value ? "Active" : "Inactive"}

                    </span>

                ),

            }

        );

        if (editPermission) {

            cols.push({

                accessor: "action",

                Header: "Actions",

                sortable: false,

                Cell: ({ row }) => (

                    <Button variant="ghost" size="sm" className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"

                        onClick={() => {

                            setEditMode(true);

                            setFormData({

                                id: row?.id || null,

                                code: row?.code || "",

                                block_name: row?.block_name || "",

                                branch: row?.branch_id || undefined,

                                notes: row?.notes || "",

                                is_active: row?.is_active ?? true,

                            });

                            setOpenDialog(true);

                        }}>

                        <Edit className="h-4 w-4" />

                    </Button>

                ),

            });

        }

        return cols;

    }, [branchEnabled, editPermission]);



    const handleSave = async () => {

        if (!formData.block_name?.trim()) {

            ToastMsg("error", "Block name is required");

            return;

        }

        const nameErr = validateMasterLocationName(formData.block_name, "Block name");
        if (nameErr) {
            ToastMsg("error", nameErr);
            return;
        }

        if (branchEnabled && !formData.branch) {

            ToastMsg("error", "Branch is required");

            return;

        }

        try {

            setLoading(true);

            const payload = { ...formData, is_draft: false };

            if (!branchEnabled) {

                delete payload.branch;

            } else if (isBranchManager && lockedBranchId) {

                payload.branch = lockedBranchId;

            }

            const response = editMode

                ? await update(MASTER_BLOCK, payload)

                : await create(MASTER_BLOCK, payload);

            if (response?.status === "success") {

                ToastMsg("success", response.message || "Block saved");

                resetForm();

                setServerTrigger((p) => !p);

            } else {

                ToastMsg("error", masterSaveErrorMessage(response, "Failed to save block"));

            }

        } finally {

            setLoading(false);

        }

    };



    return (

        <ContainerCard>

            <ModuleTitleCard

                Icon={Building}

                title="Block"

                subtitle="Manage campus blocks; link to branch when branch management is enabled"

                totalRecords={totalRecords}

                buttonEnable={!!addPermission || !!editPermission}

                setOpenDialog={openAddDialog}

                buttonName="Add Block"

            />

            <Reactable

                columns={columns}

                data={data}

                setData={setData}

                setTotalCount={setTotalRecords}

                url={MASTER_BLOCK}

                filter={branchListFilter}

                serverSide

                serverTrigger={serverTrigger}

                rowSize={editPermission ? "sm" : null}

                isCSVRequired

                isPDFRequired

                isDateRequired={false}

            />

            <DialogModal

                open={openDialog}

                onClose={resetForm}

                onSave={handleSave}

                title={editMode ? "Edit Block" : "Add Block"}

                closeLabel="Clear"

                saveLabel={editMode ? "Update" : "Save"}

                editMode={editMode}

                loading={loading}

                width="420px"

            >

                <div className="space-y-4">

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>

                            Block Name <span className="text-red-500">*</span>

                        </label>

                        <Input

                            value={formData.block_name}

                            onChange={(e) => setFormData({ ...formData, block_name: e.target.value })}

                            placeholder="Enter block name"

                            maxLength={MASTER_BLOCK_FLOOR_NAME_MAX}

                            disabled={loading}

                        />

                    </div>

                    {branchEnabled && (

                        <div className="space-y-2">

                            <label className={modalFieldLabelClass}>

                                Branch <span className="text-red-500">*</span>

                            </label>

                            <Select
                                {...masterFormSelectProps}
                                placeholder="Select branch"
                                value={formData.branch}
                                onChange={(value) => setFormData({ ...formData, branch: value })}
                                disabled={loading || isBranchManager}
                                showSearch={!isBranchManager}
                            >

                                {(isBranchManager && lockedBranchId

                                    ? [{ id: lockedBranchId, name: loginBranchName || "My Branch", is_main_branch: false }]

                                    : branches

                                ).map((br) => (

                                    <Option key={br.id} value={br.id}>

                                        {br.name}{br.is_main_branch ? " (Main)" : ""}

                                    </Option>

                                ))}

                            </Select>

                        </div>

                    )}

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>Description</label>

                        <Textarea

                            value={formData.notes}

                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}

                            placeholder="Optional notes"

                            rows={3}

                            maxLength={MASTER_NOTES_MAX_LENGTH}

                            showCharCount
                            disabled={loading}
                            className={modalTextareaClass}
                        />

                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                </div>

            </DialogModal>

        </ContainerCard>

    );

};



export default BlockMaster;


